import React, { useState, useCallback, useMemo, useId } from 'react';
import {
  ContentLayout,
  Container,
  Header,
  SpaceBetween,
  Alert,
} from '@amzn/awsui-components-console';
import SearchBar from './components/SearchBar';
import SavedSearchesBar from './components/SavedSearchesBar';
import { useSavedSearches } from './hooks/useSavedSearches';
import SearchModeSelector from './components/SearchModeSelector';
import ExactMatchToggle from './components/ExactMatchToggle';
import SearchFilterBar from './components/SearchFilterBar';
import SearchPanelFooter from './components/SearchPanelFooter';
import SearchResultsTable from './components/SearchResultsTable';
import InfoTooltip from 'src/common/ui/InfoTooltip';
import { SearchFilters } from 'src/api/graphql/searchTypes';
import {
  MIN_QUERY_LENGTH_MESSAGE,
  MIN_SEARCH_CRITERIA_MESSAGE,
} from './constants';
import {
  hasActiveFilters,
  hasMinimumSearchCriteria,
  isQueryTooShort,
  NO_FILTERS,
} from './searchFilterUtils';
import {
  DEFAULT_SORT_STATE,
  removeSourceOnlySortField,
  type SortState,
} from './sortState';

export type SearchMode = 'SOURCE' | 'TRANSLATION';

/** Named constants for SearchMode values — avoids scattering string literals. */
export const SEARCH_MODE = {
  SOURCE: 'SOURCE',
  TRANSLATION: 'TRANSLATION',
} as const satisfies Record<string, SearchMode>;

export interface SearchState {
  queryText: string;
  mode: SearchMode;
  exactMatch: boolean;
  filters: SearchFilters;
  /**
   * The sort in effect, or `null` for "no explicit sort" — the default. It is sent
   * with every search and is the ONLY thing that orders the rows; when `null` the
   * request carries no `sortBy` and the backend orders by relevance (keyword
   * search) or recency (browse). See `sortState.ts`.
   */
  sortState: SortState;
}

const DEFAULT_SEARCH_STATE: SearchState = {
  queryText: '',
  mode: SEARCH_MODE.SOURCE,
  exactMatch: false,
  filters: NO_FILTERS,
  sortState: DEFAULT_SORT_STATE,
};

const SEARCH_HELP_TEXT =
  'Search across source strings and translations in namespaces you have access to. ' +
  'Use filters to narrow results by namespace, family, string group, or status.';

/**
 * Whether this state may be submitted as a search. The length veto applies in both
 * modes; the minimum-criteria rule is scoped to SOURCE because translation mode
 * has its own required-filter rule (`translatedLocales`).
 */
const meetsSearchCriteria = (state: SearchState): boolean => {
  if (isQueryTooShort(state.queryText)) return false;
  return (
    state.mode !== SEARCH_MODE.SOURCE ||
    hasMinimumSearchCriteria(state.filters, state.queryText)
  );
};

const SearchPage: React.FC = () => {
  const [searchState, setSearchState] =
    useState<SearchState>(DEFAULT_SEARCH_STATE);
  const [triggerSearch, setTriggerSearch] = useState(0);
  /**
   * Bumped to tell `SearchResultsTable` to discard its results. A counter, not a
   * boolean, so two consecutive Clears are two distinguishable events.
   */
  const [resetResults, setResetResults] = useState(0);
  /**
   * Whether the table has results on screen, reported up by the table (which owns
   * the result data). Used only to enable "Clear" — see `canClear`.
   */
  const [resultsOnScreen, setResultsOnScreen] = useState(false);

  /**
   * Backend-backed saved searches. The bar (below) reads/removes via its own use
   * of this hook; here we take only `save` to hand to the panel footer's modal.
   * Both consumers share one Apollo cache entry, so a save re-renders the bar.
   */
  const { save: saveSearch } = useSavedSearches();

  /**
   * The ONLY path that submits a search: the Search button (in
   * `SearchPanelFooter`) and the Enter key (in `SearchBar`) both land here, plus
   * `handleLoadSavedSearch` below. Nothing else
   * on this page searches — the query text, Exact match and every filter dropdown
   * are pending edits until one of those gestures commits them.
   *
   * A bare trigger bump with no state of its own to read: the table re-runs from
   * the `searchState` of the render the bump lands in, so it always carries the
   * current dropdown selections. It resets pagination too, because cursors are
   * only valid for the criteria that produced them.
   */
  const handleSearch = useCallback(() => {
    setTriggerSearch((prev) => prev + 1);
  }, []);

  const handleQueryChange = useCallback((queryText: string) => {
    setSearchState((prev) => ({ ...prev, queryText }));
  }, []);

  const handleModeChange = useCallback((mode: SearchMode) => {
    setSearchState((prev) => {
      const nextFilters = { ...prev.filters };
      // Clear source-only filters when switching to translation mode
      if (mode === SEARCH_MODE.TRANSLATION) {
        nextFilters.statuses = undefined;
      }
      // Clear translation-only filters when switching to source mode
      if (mode === SEARCH_MODE.SOURCE) {
        nextFilters.translatedLocales = undefined;
      }
      return {
        ...prev,
        mode,
        filters: nextFilters,
        // The sort criterion is dropped too when it is source-only.
        sortState:
          mode === SEARCH_MODE.TRANSLATION
            ? removeSourceOnlySortField(prev.sortState)
            : prev.sortState,
      };
    });
    /*
     * The switch issues no request, so rows left on screen would belong to the
     * other mode's query and column shape. The controls survive on purpose — the
     * user is re-aiming the same search, not starting over.
     */
    setResetResults((prev) => prev + 1);
  }, []);

  /**
   * Records the toggle and nothing more: on its own it narrows nothing (see
   * `hasMinimumSearchCriteria`), so it is submitted with the query text rather
   * than by itself.
   */
  const handleExactMatchChange = useCallback((exactMatch: boolean) => {
    setSearchState((prev) => ({ ...prev, exactMatch }));
  }, []);

  /**
   * The ONE path every filter-dropdown change takes — the dropdowns themselves,
   * and the dependent-filter pruning in `SearchFilterBar`.
   *
   * It RECORDS the change and nothing more. Like the query text and Exact match, a
   * dropdown selection is a pending edit until the user submits it with Search or
   * Enter; no handler on this page issues a search on its own.
   *
   * A functional update is resolved INSIDE the state updater so it always reads
   * the committed filters — that is what lets `pruneInvalidSelections` run against
   * the latest selection when a fetch resolves late, rather than a stale
   * render-time closure. Returning `prev` unchanged for a no-op matters for the
   * same caller: it runs on every pass of the dependent-filter effects and returns
   * the SAME object when there is nothing to prune, and React bails out of the
   * re-render when the state is identical, so those passes cannot loop.
   */
  const handleFiltersChange = useCallback(
    (update: SearchFilters | ((prev: SearchFilters) => SearchFilters)) => {
      setSearchState((prev) => {
        const next =
          typeof update === 'function' ? update(prev.filters) : update;
        return next === prev.filters ? prev : { ...prev, filters: next };
      });
    },
    [],
  );

  /**
   * A sort change is itself a submit: the table re-queries from page 1 on every
   * `sortState` change, because cursors are only valid for the order that produced
   * them. That re-query carries whatever filters are in state, including any the
   * user selected but has not submitted yet.
   */
  const handleSortChange = useCallback((sortState: SortState) => {
    setSearchState((prev) => ({ ...prev, sortState }));
  }, []);

  const handleLoadSavedSearch = useCallback((saved: SearchState) => {
    setSearchState(saved);
    /*
     * A second submit path, honouring the same gate. The payload comes from
     * localStorage — hand-edited, or written by an older build — so an
     * empty-criteria entry must not auto-fire an unbounded query. The state is
     * still loaded; only the automatic search is suppressed.
     */
    if (meetsSearchCriteria(saved)) {
      setTriggerSearch((prev) => prev + 1);
    } else {
      /*
       * This branch only: the reset effect outranks the trigger effect in the
       * same commit, so bumping alongside `setTriggerSearch` above would cancel
       * the load's own search. A load replaces `mode` wholesale, so without a
       * reset here the previous mode's rows would sit under the loaded criteria.
       */
      setResetResults((prev) => prev + 1);
    }
  }, []);

  /**
   * Resets every narrowing control at once — filter dropdowns, Exact match and the
   * free-text query — AND discards the results with them, returning the table to
   * its pre-search empty state via `resetResults`.
   *
   * Search MODE and the SORT ORDER deliberately survive: both are presentation
   * choices rather than things being cleared. Sort survival is safe because no
   * request is issued here and the table's sort effect is gated on having results,
   * so a preserved sort cannot re-fire the discarded search.
   *
   * ISSUES NO REQUEST. A full clear leaves no query text and no filters, which is
   * exactly the unbounded browse `hasMinimumSearchCriteria` blocks, so "re-run the
   * search unfiltered" is not available — the empty state is the only reading
   * consistent with that gate. A request already IN FLIGHT is gated on the table's
   * side.
   */
  const handleClearFilters = useCallback(() => {
    setSearchState((prev) => ({
      ...prev,
      queryText: '',
      exactMatch: false,
      filters: NO_FILTERS,
    }));
    setResetResults((prev) => prev + 1);
  }, []);

  const filtersActive = useMemo(
    () =>
      hasActiveFilters(
        searchState.filters,
        searchState.queryText,
        searchState.exactMatch,
      ),
    [searchState.filters, searchState.queryText, searchState.exactMatch],
  );

  /**
   * Whether "Clear" has anything to do.
   *
   * `filtersActive` alone is not enough: it goes false as soon as the query text is
   * blank, Exact match is off and every filter array is empty, while the RESULTS of
   * the last search are still on screen. A user who empties the controls by hand
   * would otherwise be left with stale rows and the only control that dismisses
   * them greyed out. `resultsOnScreen` closes that gap.
   *
   * ENABLEMENT ONLY — pressing Clear with the controls already empty resets the
   * same state and still issues no request.
   */
  const canClear = filtersActive || resultsOnScreen;

  /*
   * Deliberate asymmetry with `filtersActive`: toggling only Exact match enables
   * "Clear" but must NOT enable "Search". See `searchFilterUtils.ts`.
   */
  const canSearch = useMemo(
    () => meetsSearchCriteria(searchState),
    [searchState],
  );

  /**
   * Why the search is refused, or `undefined` when it is not. Rendered once, in the
   * panel footer, and referenced by the query input's `aria-describedby` (see
   * `blockedReasonId` below).
   *
   * The length case is checked from `queryText` here rather than deferred to
   * `canSearch`, because it is the more specific diagnosis of the same disabled
   * button: with a filter selected a two-character term satisfies the criteria
   * rule, so the generic message would tell the user to do something they have
   * already done while the search stays refused.
   */
  const blockedReason = useMemo(() => {
    if (isQueryTooShort(searchState.queryText)) return MIN_QUERY_LENGTH_MESSAGE;
    return canSearch ? undefined : MIN_SEARCH_CRITERIA_MESSAGE;
  }, [searchState.queryText, canSearch]);

  /*
   * The footer message and the input it describes live in two different components,
   * so the id that ties them together is minted HERE — their nearest common owner —
   * rather than by either end with `useId`. Passed to the footer as the id to render
   * with, and to the input as its `aria-describedby`, and only while there IS a
   * message: a dangling reference would leave the input claiming a description that
   * is not in the document.
   */
  const blockedReasonId = useId();

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          info={<InfoTooltip content={SEARCH_HELP_TEXT} />}
          description="Search across all strings and translations by ID, text, locale, or status."
        >
          Search Strings
        </Header>
      }
    >
      <SpaceBetween size="l">
        <Alert type="info" statusIconAriaLabel="Info">
          Results are restricted to namespaces you have access to. If you need
          access to additional namespaces, contact the namespace owner via
          Bindle.
        </Alert>

        {/* Panel 1 — everything that narrows the search */}
        <Container
          /*
            No `header`: the page's own `h1` "Search Strings" and its description
            sit directly above this panel, so a panel title would restate them.
          */
          footer={
            <SearchPanelFooter
              currentSearchState={searchState}
              canSearch={canSearch}
              canClear={canClear}
              onSearch={handleSearch}
              onClear={handleClearFilters}
              blockedReason={blockedReason}
              blockedReasonId={blockedReasonId}
              onSaveSearch={saveSearch}
            />
          }
        >
          <SpaceBetween size="m">
            <SearchModeSelector
              mode={searchState.mode}
              onModeChange={handleModeChange}
            />

            <SearchBar
              queryText={searchState.queryText}
              onQueryChange={handleQueryChange}
              onSearch={handleSearch}
              canSearch={canSearch}
              ariaDescribedby={blockedReason ? blockedReasonId : undefined}
            />

            {/*
              Below the input, because it qualifies the text that was just typed
              there rather than the choice of corpus above it.
            */}
            <ExactMatchToggle
              exactMatch={searchState.exactMatch}
              onExactMatchChange={handleExactMatchChange}
            />

            <SearchFilterBar
              mode={searchState.mode}
              filters={searchState.filters}
              onFiltersChange={handleFiltersChange}
            />

            {/*
              Saved searches close the panel, below the filter dropdowns: a chip
              replaces every control above it, so it reads as an alternative to
              filling the panel in rather than as one more field in it. This
              component returns null when nothing is saved, so the row is absent
              rather than empty on a first visit.
            */}
            <SavedSearchesBar onLoadSearch={handleLoadSavedSearch} />
          </SpaceBetween>
        </Container>

        {/*
         * Panel 2 — results. The table renders its own Cloudscape container, so
         * wrapping it in another <Container> would double the border and padding.
         */}
        <SearchResultsTable
          searchState={searchState}
          triggerSearch={triggerSearch}
          resetResults={resetResults}
          onHasSearchedChange={setResultsOnScreen}
          onSortChange={handleSortChange}
        />
      </SpaceBetween>
    </ContentLayout>
  );
};

export default SearchPage;
